import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/lib/formatDate';
import { StatusChip, type StatusTone } from '@/components/ui/status-chip';
import { Segmented } from '@/components/ui/segmented';
import { PageHeader } from '@/components/ui/page-header';
import {
  platformAdminGetTenants, platformAdminGetTenant, platformAdminCreateTenant,
  platformAdminUpdateTenant, platformAdminUpdateTenantLicense, platformAdminResendInvite,
  platformAdminCheckAvailability, platformAdminLogin, getPlatformToken, clearPlatformToken,
  platformAdminGetTenantHistory, platformAdminGetLicenseFile,
  platformAdminGetSignupRequests, platformAdminUpdateSignupRequest, platformAdminGetStrengthFixes,
} from '@/lib/api';
import StrengthFixesPanel from '@/components/PlatformAdmin/StrengthFixesPanel';
import { useAuth } from '@/context/AuthContext';
import { PLAN_FEATURES } from '@/hooks/usePermission';
import { MEDICAL_SPECIALTIES } from '@/lib/constants';
import { Plus, Pencil, ShieldCheck, AlertTriangle, CheckCircle, Building2, Lock, Copy, Eye, EyeOff, Mail, Stethoscope, Link2, MoreHorizontal, Download, History } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from "@/hooks/usePageTitle";
import { loginLinkUrl } from '@/lib/clinicBrand';

// ─── Toast helper (same pattern as SettingsPage) ───────────────────────────────

function useNotify() {
  const [note, setNote] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);
  return { note, ok: (msg: string) => setNote({ type: 'success', msg }), err: (msg: string) => setNote({ type: 'error', msg }) };
}

function Notify({ note }: { note: { type: 'success' | 'error'; msg: string } | null }) {
  if (!note) return null;
  return (
    <Alert className={cn('mb-4', note.type === 'success' ? 'border-success/30 bg-success/5' : 'border-destructive/30 bg-destructive/5')}>
      {note.type === 'success'
        ? <CheckCircle className="h-4 w-4 text-success" />
        : <AlertTriangle className="h-4 w-4 text-destructive" />}
      <AlertDescription className={note.type === 'success' ? 'text-success' : 'text-destructive'}>{note.msg}</AlertDescription>
    </Alert>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const PLANS = ['basic', 'clinic', 'hospital', 'enterprise'] as const;
const STATUSES = ['active', 'suspended', 'cancelled'] as const;

// Plans are labels, not states — they all read neutral; status carries the colour
const STATUS_TONE: Record<string, StatusTone> = {
  active:    'success',
  suspended: 'warning',
  cancelled: 'alert',
};

type Tenant = {
  id: string;
  name: string;
  subdomain: string | null;
  email: string | null;
  status: string;
  plan: string;
  license_expires_at: string | null;
  max_users: number | null;
  max_clinics: number | null;
  license_features: Record<string, boolean> | null;
  created_at: string;
  used_users: number;
  used_clinics: number;
  last_active_at: string | null;
};

// ─── Tenant list helpers ───────────────────────────────────────────────────────

const INACTIVE_DAYS = 14;
const EXPIRING_DAYS = 30;

function daysUntil(date: string): number {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(date); end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
}

function daysSince(date: string): number {
  return Math.floor((Date.now() - new Date(date).getTime()) / 86_400_000);
}

const isExpired  = (t: Tenant) => !!t.license_expires_at && daysUntil(t.license_expires_at) < 0;
const isExpiring = (t: Tenant) => !!t.license_expires_at && !isExpired(t) && daysUntil(t.license_expires_at) <= EXPIRING_DAYS;
const isInactive = (t: Tenant) => t.status === 'active' && (!t.last_active_at || daysSince(t.last_active_at) >= INACTIVE_DAYS);

const TENANT_FILTERS = [
  { key: 'all',       label: 'All',                          match: (_: Tenant) => true },
  { key: 'expiring',  label: `Expiring ≤${EXPIRING_DAYS}d`,  match: isExpiring },
  { key: 'expired',   label: 'Expired',                      match: isExpired },
  { key: 'suspended', label: 'Suspended',                    match: (t: Tenant) => t.status === 'suspended' },
  { key: 'inactive',  label: `Inactive ${INACTIVE_DAYS}d+`,  match: isInactive },
] as const;

function ExpiryCell({ t }: { t: Tenant }) {
  if (!t.license_expires_at) return <span className="text-muted-foreground">No expiry</span>;
  const days = daysUntil(t.license_expires_at);
  const date = formatDate(t.license_expires_at);
  if (days < 0) return <StatusChip tone="alert">Expired {date}</StatusChip>;
  if (days <= EXPIRING_DAYS) return <StatusChip tone="warning">{days === 0 ? 'Today' : `In ${days}d`} · {date}</StatusChip>;
  return <span className="text-muted-foreground tabular-nums">{date}</span>;
}

function UsageCell({ used, limit }: { used: number; limit: number | null }) {
  // At the limit = can't add more (bold); over it = limit was lowered below current usage (red)
  const tone = limit == null || used < limit ? 'text-muted-foreground'
    : used > limit ? 'font-medium text-destructive' : 'font-medium text-foreground';
  return <span className={cn('font-mono tabular-nums', tone)}>{used} / {limit ?? '∞'}</span>;
}

function LastActiveCell({ t }: { t: Tenant }) {
  if (!t.last_active_at) return <span className={cn(t.status === 'active' ? 'text-warning' : 'text-muted-foreground')}>Never</span>;
  const days = daysSince(t.last_active_at);
  const label = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days}d ago`;
  return <span className={cn('tabular-nums', isInactive(t) ? 'text-warning' : 'text-muted-foreground')}>{label}</span>;
}

const EXPIRY_PRESETS = [
  { value: 'none',   label: 'No expiry' },
  { value: 'trial',  label: '14-day trial' },
  { value: 'year',   label: '1 year' },
  { value: 'custom', label: 'Custom date' },
] as const;

const FEATURE_LABELS: Record<string, string> = {
  ipd: 'IPD', reports: 'Reports', role_access: 'Role access', peds_calculator: 'Peds calculator',
  drug_interactions: 'Drug interactions', data_export: 'Data export', gst_invoice: 'GST invoice', multi_branch: 'Multi-branch',
};

const EMPTY_CREATE_FORM = {
  name: '', subdomain: '', mrn_prefix: '', email: '', phone: '', address: '',
  plan: 'hospital' as typeof PLANS[number],
  max_users: '', max_clinics: '',
  expiry: 'none' as typeof EXPIRY_PRESETS[number]['value'], license_expires_at: '',
  admin_name: '', admin_email: '',
  admin_is_doctor: false, admin_specialty: 'General Practitioner', admin_registration_no: '',
  signup_request_id: '',
};

type SignupRequest = {
  id: string; full_name: string; phone: string; email: string | null;
  status: 'new' | 'contacted' | 'converted' | 'dismissed';
  converted_tenant_id: string | null; created_at: string;
};

const LEAD_STATUS_TONE: Record<SignupRequest['status'], StatusTone> = {
  new:       'accent',
  contacted: 'warning',
  converted: 'success',
  dismissed: 'neutral',
};

type HistoryEntry = {
  id: number; action: string; at: string;
  actor_name: string | null; actor_email: string | null;
  before_data: Record<string, any> | null; after_data: Record<string, any> | null;
  changed_fields: string[] | null;
};

const HISTORY_FIELD_LABELS: Record<string, string> = {
  plan: 'Plan', status: 'Status', license_expires_at: 'Expiry', max_users: 'Max users',
  max_clinics: 'Max clinics', license_features: 'Feature overrides', name: 'Name',
  email: 'Contact email', phone: 'Phone', address: 'Address', mrn_prefix: 'MRN prefix',
};

function formatHistoryValue(field: string, v: unknown): string {
  if (v == null || v === '') return '—';
  if (field === 'license_expires_at') return formatDate(String(v));
  if (typeof v === 'object') return 'changed';
  return String(v);
}

type InviteResult = {
  tenantName: string; adminName: string; adminEmail: string;
  inviteLink: string; expiresInDays: number; emailScheduled: boolean;
  /** The clinic's own sign-in page for its staff; empty when no link name was set */
  loginLink: string;
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const MRN_PREFIX_RE = /^[A-Z0-9]{2,5}$/;

/** "Sunrise Child Care" → "SCC", "Apollo" → "APO" */
function suggestMrnPrefix(name: string): string {
  const words = name.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/)
    .filter(w => w && !['THE', 'AND', 'OF', 'DR'].includes(w));
  const prefix = words.length >= 2 ? words.map(w => w[0]).join('') : (words[0] ?? '').slice(0, 3);
  return prefix.slice(0, 5);
}

/** "Sunrise Child Care" → "sunrise-child-care" */
function suggestSubdomain(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);
}

function dateFromToday({ days = 0, years = 0 }: { days?: number; years?: number }): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
}

function inviteMessage(r: InviteResult): string {
  return `Hi ${r.adminName}, your ${r.tenantName} workspace is ready.\nLogin email: ${r.adminEmail}\nSet your password: ${r.inviteLink}\n(This link expires in ${r.expiresInDays} days.)${r.loginLink ? `\nYour staff sign in at: ${r.loginLink}` : ''}`;
}

async function copyToClipboard(text: string) {
  try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
}

function CopyField({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [visible, setVisible] = useState(!secret);
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={visible ? value : '•'.repeat(Math.min(value.length, 16))}
          className="text-xs font-mono h-8 flex-1"
        />
        {secret && (
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setVisible(v => !v)}>
            {visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
        )}
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handle}>
          {copied ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

// ─── Platform secret challenge gate ───────────────────────────────────────────

function PlatformChallenge({ onUnlocked }: { onUnlocked: () => void }) {
  const { currentUser } = useAuth();
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await platformAdminLogin(currentUser?.email ?? '', password, secret);
      onUnlocked();
    } catch (e: any) {
      setError(e?.message || 'Authentication failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8 rounded-lg border border-border bg-card">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-lg font-semibold">Platform admin access</h1>
          <p className="text-sm text-muted-foreground">Re-enter your password and the platform secret to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Platform Secret</Label>
            <Input type="password" value={secret} onChange={e => setSecret(e.target.value)} required placeholder="X-Platform-Secret" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive">
              <AlertTriangle className="w-3.5 h-3.5" />{error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={saving}>
            {saving ? 'Verifying…' : 'Unlock'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ─── Onboarding wizard ─────────────────────────────────────────────────────────

const WIZARD_STEPS = ['Clinic', 'Plan', 'Owner'] as const;

function AvailabilityHint({ available }: { available?: boolean }) {
  if (available === undefined) return null;
  return available
    ? <p className="text-xs text-success">Available</p>
    : <p className="text-xs text-destructive">Already in use</p>;
}

function OnboardTenantWizard({ open, onOpenChange, onCreated, prefill }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (result: InviteResult) => void;
  /** Values carried over when converting a sign-up request */
  prefill?: Partial<typeof EMPTY_CREATE_FORM>;
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  // Prefix/subdomain follow the tenant name until edited by hand
  const [edited, setEdited] = useState({ mrn_prefix: false, subdomain: false });
  const [avail, setAvail] = useState<{ mrn_prefix?: boolean; subdomain?: boolean; email?: boolean }>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm({ ...EMPTY_CREATE_FORM, ...prefill });
    setEdited({ mrn_prefix: false, subdomain: false });
    setAvail({});
    setError('');
  }, [open, prefill]);

  // Live uniqueness checks (debounced)
  useEffect(() => {
    if (!open) return;
    const params = {
      mrn_prefix: MRN_PREFIX_RE.test(form.mrn_prefix) ? form.mrn_prefix : undefined,
      subdomain: form.subdomain || undefined,
      email: form.admin_email.includes('@') ? form.admin_email : undefined,
    };
    if (!params.mrn_prefix && !params.subdomain && !params.email) { setAvail({}); return; }
    const t = setTimeout(async () => {
      try { setAvail(await platformAdminCheckAvailability(params)); } catch { setAvail({}); }
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.mrn_prefix, form.subdomain, form.admin_email]);

  const set = (patch: Partial<typeof EMPTY_CREATE_FORM>) => setForm(f => ({ ...f, ...patch }));
  const setName = (name: string) => setForm(f => ({
    ...f,
    name,
    mrn_prefix: edited.mrn_prefix ? f.mrn_prefix : suggestMrnPrefix(name),
    subdomain: edited.subdomain ? f.subdomain : suggestSubdomain(name),
  }));

  const stepError = (): string => {
    if (step === 0) {
      if (!form.name.trim()) return 'Clinic name is required';
      if (!MRN_PREFIX_RE.test(form.mrn_prefix)) return 'MRN prefix must be 2–5 letters or digits';
      if (avail.mrn_prefix === false) return 'MRN prefix is already in use';
      if (avail.subdomain === false) return 'Login link is already in use';
    }
    if (step === 1 && form.expiry === 'custom' && !form.license_expires_at) return 'Pick an expiry date';
    if (step === 2) {
      if (!form.admin_name.trim()) return 'Owner name is required';
      if (!form.admin_email.includes('@')) return 'A valid owner email is required';
      if (avail.email === false) return 'Owner email is already in use';
    }
    return '';
  };

  const next = () => {
    const e = stepError();
    setError(e);
    if (!e) setStep(s => s + 1);
  };

  const create = async () => {
    const e = stepError();
    setError(e);
    if (e) return;

    const expiresAt =
      form.expiry === 'trial'  ? dateFromToday({ days: 14 }) :
      form.expiry === 'year'   ? dateFromToday({ years: 1 }) :
      form.expiry === 'custom' ? form.license_expires_at :
      undefined;

    setSaving(true);
    try {
      const result = await platformAdminCreateTenant({
        name: form.name.trim(),
        subdomain: form.subdomain || undefined,
        mrn_prefix: form.mrn_prefix,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        plan: form.plan,
        max_users: form.max_users ? Number(form.max_users) : undefined,
        max_clinics: form.max_clinics ? Number(form.max_clinics) : undefined,
        license_expires_at: expiresAt,
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim(),
        admin_is_doctor: form.admin_is_doctor || undefined,
        admin_specialty: form.admin_is_doctor ? form.admin_specialty : undefined,
        admin_registration_no: form.admin_is_doctor ? form.admin_registration_no || undefined : undefined,
        signup_request_id: form.signup_request_id || undefined,
      });
      onCreated({
        tenantName: form.name.trim(),
        adminName: form.admin_name.trim(),
        adminEmail: form.admin_email.trim(),
        inviteLink: result?.inviteLink ?? '',
        expiresInDays: result?.inviteExpiresInDays ?? 7,
        emailScheduled: result?.emailScheduled ?? false,
        loginLink: form.subdomain ? loginLinkUrl(form.subdomain) : '',
      });
    } catch (e: any) { setError(e?.message || 'Failed to create tenant'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Onboard a clinic</DialogTitle></DialogHeader>

        <div className="flex items-center gap-2 text-xs">
          {WIZARD_STEPS.map((label, i) => (
            <div key={label} className={cn(
              'flex-1 rounded-full px-3 py-1 text-center font-medium',
              i === step ? 'bg-primary text-primary-foreground' : i < step ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
            )}>
              {i + 1}. {label}
            </div>
          ))}
        </div>

        <div className="space-y-3 py-1">
          {step === 0 && (
            <>
              <Field label="Clinic / Hospital Name *">
                <Input autoFocus value={form.name} placeholder="e.g. Sunrise Child Care" onChange={e => setName(e.target.value)} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="MRN Prefix *">
                  <Input value={form.mrn_prefix} maxLength={5} placeholder="e.g. SCC"
                    onChange={e => { setEdited(x => ({ ...x, mrn_prefix: true })); set({ mrn_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }); }} />
                  <AvailabilityHint available={avail.mrn_prefix} />
                </Field>
                <Field label="Login link">
                  <Input value={form.subdomain}
                    onChange={e => { setEdited(x => ({ ...x, subdomain: true })); set({ subdomain: e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '') }); }} />
                  <AvailabilityHint available={form.subdomain ? avail.subdomain : undefined} />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">Patient IDs will look like {form.mrn_prefix || 'ABC'}1001. The prefix can't be reused by another clinic.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Contact Email"><Input type="email" value={form.email} onChange={e => set({ email: e.target.value })} /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={e => set({ phone: e.target.value })} /></Field>
              </div>
              <Field label="Address"><Input value={form.address} onChange={e => set({ address: e.target.value })} /></Field>
            </>
          )}

          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {PLANS.map(p => (
                  <button key={p} type="button" onClick={() => set({ plan: p })}
                    className={cn('rounded-lg border p-3 text-left transition-colors',
                      form.plan === p ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-muted/50')}>
                    <div className="text-sm font-semibold capitalize">{p}</div>
                    <div className="mt-1 text-2xs leading-snug text-muted-foreground">
                      {Object.entries(PLAN_FEATURES[p]).filter(([, on]) => on).map(([k]) => FEATURE_LABELS[k] ?? k).join(' · ') || 'Core OPD only'}
                    </div>
                  </button>
                ))}
              </div>
              <Field label="License">
                <div className="flex flex-wrap gap-2">
                  {EXPIRY_PRESETS.map(opt => (
                    <Button key={opt.value} type="button" size="sm" variant={form.expiry === opt.value ? 'default' : 'outline'}
                      onClick={() => set({ expiry: opt.value })}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </Field>
              {form.expiry === 'custom' && (
                <Field label="Expires On"><Input type="date" value={form.license_expires_at} onChange={e => set({ license_expires_at: e.target.value })} /></Field>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Max Users"><Input type="number" min={0} placeholder="Unlimited" value={form.max_users} onChange={e => set({ max_users: e.target.value })} /></Field>
                <Field label="Max Clinics"><Input type="number" min={0} placeholder="Unlimited" value={form.max_clinics} onChange={e => set({ max_clinics: e.target.value })} /></Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Owner Name *"><Input autoFocus value={form.admin_name} onChange={e => set({ admin_name: e.target.value })} /></Field>
                <Field label="Owner Email *">
                  <Input type="email" value={form.admin_email} onChange={e => set({ admin_email: e.target.value })} />
                  <AvailabilityHint available={avail.email} />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">
                The owner receives a link to set their own password — no password to create or share.
              </p>
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 mt-2 mb-1">
                  <Checkbox id="admin_is_doctor" checked={form.admin_is_doctor}
                    onCheckedChange={(c) => set({ admin_is_doctor: c === true })} />
                  <Label htmlFor="admin_is_doctor" className="text-sm cursor-pointer flex items-center gap-1.5">
                    <Stethoscope className="w-3.5 h-3.5 text-muted-foreground" />
                    Owner is also a practicing doctor
                  </Label>
                </div>
                {form.admin_is_doctor && (
                  <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
                    <Field label="Specialization">
                      <Select value={form.admin_specialty} onValueChange={v => set({ admin_specialty: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MEDICAL_SPECIALTIES.map(sp => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Registration No.">
                      <Input placeholder="MCI / State Reg." value={form.admin_registration_no} onChange={e => set({ admin_registration_no: e.target.value })} />
                    </Field>
                  </div>
                )}
              </div>
            </>
          )}

          {error && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{error}</div>}
        </div>

        <DialogFooter>
          {step > 0
            ? <Button variant="outline" onClick={() => { setError(''); setStep(s => s - 1); }} disabled={saving}>Back</Button>
            : <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>}
          {step < WIZARD_STEPS.length - 1
            ? <Button onClick={next}>Next</Button>
            : <Button onClick={create} disabled={saving}>{saving ? 'Creating…' : 'Create & send invite'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function PlatformAdminPage() {
  usePageTitle('Platform admin');
  const { note, ok, err } = useNotify();
  const [unlocked, setUnlocked] = useState(() => !!getPlatformToken());
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  // Onboarding wizard (prefill is set when converting a sign-up request)
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<Partial<typeof EMPTY_CREATE_FORM> | undefined>();

  // Tenants / sign-up requests view
  const [view, setView] = useState<'tenants' | 'leads' | 'catalog'>('tenants');
  // Groups of clinic strength corrections waiting for review (badge on the tab)
  const [catalogPending, setCatalogPending] = useState(0);
  const [leads, setLeads] = useState<SignupRequest[]>([]);
  const [showClosedLeads, setShowClosedLeads] = useState(false);

  // Manage dialog: change history
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);

  // Manage tenant modal
  const [manageTenant, setManageTenant] = useState<Tenant | null>(null);
  const [manageForm, setManageForm] = useState({
    name: '', email: '', phone: '', address: '', status: 'active' as typeof STATUSES[number],
    plan: 'hospital' as typeof PLANS[number],
    max_users: '', max_clinics: '', license_expires_at: '', subdomain: '',
  });
  const [manageUsage, setManageUsage] = useState<{ clinics: number; doctors: number; patients: number; users: number } | null>(null);
  const [manageSaving, setManageSaving] = useState(false);

  // New set-password link for the owner (inside manage dialog)
  const [resendSaving, setResendSaving] = useState(false);
  const [resendResult, setResendResult] = useState<{ adminEmail: string; inviteLink: string; emailSent: boolean } | null>(null);

  // Invite modal (shown after tenant creation)
  const [invite, setInvite] = useState<InviteResult | null>(null);

  // Tenant list filter + row quick-action results
  const [filter, setFilter] = useState<typeof TENANT_FILTERS[number]['key']>('all');
  const [rowInvite, setRowInvite] = useState<{ tenantName: string; adminEmail: string; inviteLink: string; emailSent: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformAdminGetTenants();
      setTenants(data ?? []);
    } catch (e: any) {
      if (e?.status === 403 || e?.message?.includes('Platform admin')) {
        clearPlatformToken();
        setUnlocked(false);
      } else {
        err('Failed to load tenants');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (unlocked) load(); }, [unlocked, load]);

  const loadLeads = useCallback(async () => {
    try { setLeads((await platformAdminGetSignupRequests()) ?? []); } catch { /* inbox is secondary — keep last list */ }
  }, []);

  useEffect(() => { if (unlocked) loadLeads(); }, [unlocked, loadLeads]);

  useEffect(() => {
    if (!unlocked) return;
    platformAdminGetStrengthFixes('pending').then(rows => {
      // One badge per correction group (same drug, ingredient and values), matching the panel
      const groups = new Set((rows ?? []).map(r => [r.drug_id, r.molecule_id, r.dose_value, r.dose_unit, r.per_volume_value].join('|')));
      setCatalogPending(groups.size);
    }).catch(() => { /* badge is secondary */ });
  }, [unlocked]);

  if (!unlocked) return <PlatformChallenge onUnlocked={() => setUnlocked(true)} />;

  const openManage = async (t: Tenant) => {
    setResendResult(null);
    setManageTenant(t);
    setManageForm({
      name: t.name,
      email: t.email || '',
      phone: '',
      address: '',
      status: (t.status as typeof STATUSES[number]) || 'active',
      plan: (t.plan as typeof PLANS[number]) || 'hospital',
      max_users: t.max_users != null ? String(t.max_users) : '',
      max_clinics: t.max_clinics != null ? String(t.max_clinics) : '',
      license_expires_at: t.license_expires_at ? t.license_expires_at.slice(0, 10) : '',
      subdomain: t.subdomain || '',
    });
    setManageUsage(null);
    setHistory(null);
    platformAdminGetTenantHistory(t.id).then(h => setHistory(h ?? [])).catch(() => setHistory([]));
    try {
      const full = await platformAdminGetTenant(t.id);
      if (full) {
        setManageForm(f => ({
          ...f,
          phone: full.phone || '',
          address: full.address || '',
        }));
        setManageUsage(full.usage || null);
      }
    } catch { /* keep defaults from list row */ }
  };

  const handleManageSave = async () => {
    if (!manageTenant) return;
    setManageSaving(true);
    try {
      await platformAdminUpdateTenant(manageTenant.id, {
        name: manageForm.name,
        email: manageForm.email || null,
        phone: manageForm.phone || null,
        address: manageForm.address || null,
        status: manageForm.status,
        subdomain: manageForm.subdomain,
      });
      await platformAdminUpdateTenantLicense(manageTenant.id, {
        plan: manageForm.plan,
        max_users: manageForm.max_users ? Number(manageForm.max_users) : null,
        max_clinics: manageForm.max_clinics ? Number(manageForm.max_clinics) : null,
        license_expires_at: manageForm.license_expires_at || null,
      });
      ok('Tenant updated');
      setManageTenant(null);
      load();
    } catch (e: any) { err(e?.message || 'Failed to update tenant'); }
    finally { setManageSaving(false); }
  };

  const quickLicenseUpdate = async (t: Tenant, patch: Record<string, unknown>, message: string) => {
    try {
      await platformAdminUpdateTenantLicense(t.id, patch);
      ok(message);
      load();
    } catch (e: any) { err(e?.message || 'Failed to update tenant'); }
  };

  const extendOneYear = (t: Tenant) => {
    // Extend from the current expiry if still in the future, otherwise from today
    const base = t.license_expires_at && daysUntil(t.license_expires_at) > 0 ? new Date(t.license_expires_at) : new Date();
    base.setFullYear(base.getFullYear() + 1);
    const next = base.toLocaleDateString('en-CA');
    quickLicenseUpdate(t, { license_expires_at: next }, `${t.name} extended to ${formatDate(next)}`);
  };

  const toggleSuspend = (t: Tenant) => {
    if (t.status === 'suspended') {
      quickLicenseUpdate(t, { status: 'active' }, `${t.name} reactivated`);
    } else if (window.confirm(`Suspend ${t.name}? Their users will lose access until reactivated.`)) {
      quickLicenseUpdate(t, { status: 'suspended' }, `${t.name} suspended`);
    }
  };

  const resendInviteFromRow = async (t: Tenant) => {
    try {
      const result = await platformAdminResendInvite(t.id);
      setRowInvite({ tenantName: t.name, adminEmail: result?.admin?.email ?? '', inviteLink: result?.inviteLink ?? '', emailSent: result?.emailSent ?? false });
    } catch (e: any) { err(e?.message || 'Failed to create set-password link'); }
  };

  const setLeadStatus = async (lead: SignupRequest, status: 'new' | 'contacted' | 'dismissed') => {
    try {
      await platformAdminUpdateSignupRequest(lead.id, status);
      loadLeads();
    } catch (e: any) { err(e?.message || 'Failed to update request'); }
  };

  const convertLead = (lead: SignupRequest) => {
    setCreatePrefill({
      admin_name: lead.full_name,
      admin_email: lead.email ?? '',
      phone: lead.phone,
      signup_request_id: lead.id,
    });
    setCreateOpen(true);
  };

  const downloadLicenseFile = async () => {
    if (!manageTenant) return;
    try {
      const file = await platformAdminGetLicenseFile(manageTenant.id);
      const url = URL.createObjectURL(new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `license-${manageTenant.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { err(e?.message || 'Failed to create license file'); }
  };

  const handleResendInvite = async () => {
    if (!manageTenant) return;
    setResendSaving(true);
    setResendResult(null);
    try {
      const result = await platformAdminResendInvite(manageTenant.id);
      setResendResult({ adminEmail: result?.admin?.email ?? '', inviteLink: result?.inviteLink ?? '', emailSent: result?.emailSent ?? false });
      ok('New set-password link created');
    } catch (e: any) { err(e?.message || 'Failed to create set-password link'); }
    finally { setResendSaving(false); }
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-6xl mx-auto space-y-4">
      <PageHeader
        icon={ShieldCheck}
        title="Platform admin"
        subtitle={loading ? 'Loading…' : (
          <>
            <span className="tabular-nums">{tenants.length}</span> tenant{tenants.length !== 1 ? 's' : ''}
            {tenants.filter(isExpiring).length > 0 && <> · <span className="tabular-nums">{tenants.filter(isExpiring).length}</span> expiring soon</>}
            {tenants.filter(isExpired).length > 0 && <> · <span className="tabular-nums">{tenants.filter(isExpired).length}</span> expired</>}
          </>
        )}
        actions={
          <Button size="sm" className="gap-1.5" onClick={() => { setCreatePrefill(undefined); setCreateOpen(true); }}>
            <Plus className="w-4 h-4" /> Onboard clinic
          </Button>
        }
      />

      <div role="tablist" aria-label="Platform admin sections" className="flex gap-5 border-b border-border overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {([['tenants', 'Tenants'], ['leads', 'Sign-up requests'], ['catalog', 'Catalog fixes']] as const).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)}
            className={cn('flex items-center gap-1.5 pb-2 pt-1 touch:min-h-10 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors',
              view === key ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {label}
            {key === 'tenants' && <span className="tabular-nums text-muted-foreground">{tenants.length}</span>}
            {key === 'leads' && leads.some(l => l.status === 'new') && (
              <StatusChip tone="accent">{leads.filter(l => l.status === 'new').length} new</StatusChip>
            )}
            {key === 'catalog' && catalogPending > 0 && (
              <StatusChip tone="accent">{catalogPending} to review</StatusChip>
            )}
          </button>
        ))}
      </div>

      <Notify note={note} />

      {view === 'catalog' && <StrengthFixesPanel ok={ok} err={err} onPendingCount={setCatalogPending} />}

      {view === 'leads' && (
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 touch:min-h-10 text-xs text-muted-foreground cursor-pointer">
            <Checkbox checked={showClosedLeads} onCheckedChange={c => setShowClosedLeads(c === true)} />
            Show converted and dismissed
          </label>
          {leads.filter(l => showClosedLeads || l.status === 'new' || l.status === 'contacted').length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">No open sign-up requests.</div>
          ) : (
            <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="h-8 border-b border-border bg-muted/40">
                    {['Name', 'Phone', 'Email', 'Received', 'Status', ''].map(h => (
                      <th key={h} scope="col" className="px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.filter(l => showClosedLeads || l.status === 'new' || l.status === 'contacted').map(lead => (
                    <tr key={lead.id} className="h-10 border-b border-divider last:border-0">
                      <td className="px-3 py-1 font-medium whitespace-nowrap">{lead.full_name}</td>
                      <td className="px-3 py-1 tabular-nums whitespace-nowrap"><a href={`tel:${lead.phone}`} className="touch-target text-primary hover:underline">{lead.phone}</a></td>
                      <td className="px-3 py-1 text-muted-foreground">{lead.email || '—'}</td>
                      <td className="px-3 py-1 text-muted-foreground tabular-nums whitespace-nowrap">{formatDate(lead.created_at)}</td>
                      <td className="px-3 py-1">
                        <StatusChip tone={LEAD_STATUS_TONE[lead.status]} className="capitalize">{lead.status}</StatusChip>
                      </td>
                      <td className="px-3 py-1">
                        {lead.status !== 'converted' && (
                          <div className="flex items-center justify-end gap-1">
                            {lead.status === 'new' && (
                              <Button variant="ghost" size="sm" onClick={() => setLeadStatus(lead, 'contacted')}>Mark contacted</Button>
                            )}
                            {lead.status === 'dismissed' ? (
                              <Button variant="ghost" size="sm" onClick={() => setLeadStatus(lead, 'new')}>Reopen</Button>
                            ) : (
                              <>
                                <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setLeadStatus(lead, 'dismissed')}>Dismiss</Button>
                                <Button size="sm" className="whitespace-nowrap" onClick={() => convertLead(lead)}>Convert to tenant</Button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'tenants' && (<>

      {!loading && tenants.length > 0 && (
        <div className="max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Segmented<typeof TENANT_FILTERS[number]['key']>
            aria-label="Filter tenants"
            value={filter}
            onChange={setFilter}
            options={TENANT_FILTERS.map(f => ({
              value: f.key,
              label: <>{f.label} <span className="tabular-nums text-muted-foreground">{tenants.filter(f.match).length}</span></>,
            }))}
          />
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : tenants.length === 0 ? (
        <div className="rounded-lg border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">No tenants yet.</div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="h-8 border-b border-border bg-muted/40">
                {['Tenant', 'Plan', 'Status', 'Expires', 'Users', 'Clinics', 'Last active', ''].map(h => (
                  <th key={h} scope="col" className={cn('px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap', (h === 'Users' || h === 'Clinics') && 'text-right')}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.filter(TENANT_FILTERS.find(f => f.key === filter)!.match).map((t) => (
                <tr key={t.id} className="h-10 border-b border-divider last:border-0">
                  <td className="px-3 py-1">
                    <div className="flex items-baseline gap-2 min-w-0 whitespace-nowrap">
                      <Building2 className="w-3.5 h-3.5 text-muted-foreground self-center flex-shrink-0" aria-hidden />
                      <span className="font-medium text-foreground">{t.name}</span>
                      <span className="text-xs text-muted-foreground truncate max-w-[14rem]">{t.email || t.subdomain || '—'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1">
                    <StatusChip tone="neutral" className="capitalize">{t.plan}</StatusChip>
                  </td>
                  <td className="px-3 py-1">
                    <StatusChip tone={STATUS_TONE[t.status] ?? 'neutral'} className="capitalize">{t.status}</StatusChip>
                  </td>
                  <td className="px-3 py-1 text-xs whitespace-nowrap"><ExpiryCell t={t} /></td>
                  <td className="px-3 py-1 text-xs text-right whitespace-nowrap"><UsageCell used={t.used_users} limit={t.max_users} /></td>
                  <td className="px-3 py-1 text-xs text-right whitespace-nowrap"><UsageCell used={t.used_clinics} limit={t.max_clinics} /></td>
                  <td className="px-3 py-1 text-xs whitespace-nowrap"><LastActiveCell t={t} /></td>
                  <td className="px-3 py-1">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Manage" aria-label={`Manage ${t.name}`} onClick={() => openManage(t)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Quick actions" aria-label={`Quick actions for ${t.name}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => extendOneYear(t)}>Extend 1 year</DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>Change plan</DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuRadioGroup value={t.plan}
                                onValueChange={plan => plan !== t.plan && quickLicenseUpdate(t, { plan }, `${t.name} moved to ${plan}`)}>
                                {PLANS.map(p => <DropdownMenuRadioItem key={p} value={p} className="capitalize">{p}</DropdownMenuRadioItem>)}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuItem onClick={() => toggleSuspend(t)}>
                            {t.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => resendInviteFromRow(t)}>Send set-password link</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openManage(t)}>Manage…</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {/* Set-password link from a row quick action */}
      <Dialog open={!!rowInvite} onOpenChange={v => { if (!v) setRowInvite(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Set-password link — {rowInvite?.tenantName}</DialogTitle></DialogHeader>
          {rowInvite && (
            <div className="space-y-3 py-1">
              <CopyField label="Set-password Link" value={rowInvite.inviteLink} />
              <div className={cn('flex items-center gap-2 text-xs rounded px-2 py-1.5',
                rowInvite.emailSent ? 'bg-success/5 text-success border border-success/30' : 'bg-highlight text-highlight-foreground border border-highlight-foreground/20'
              )}>
                {rowInvite.emailSent
                  ? <><CheckCircle className="w-3.5 h-3.5" />Link emailed to {rowInvite.adminEmail}.</>
                  : <><AlertTriangle className="w-3.5 h-3.5" />Email not configured — copy the link and share it with {rowInvite.adminEmail}.</>}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setRowInvite(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding wizard */}
      <OnboardTenantWizard
        open={createOpen}
        onOpenChange={setCreateOpen}
        prefill={createPrefill}
        onCreated={(result) => { setCreateOpen(false); setInvite(result); load(); if (createPrefill) loadLeads(); }}
      />

      {/* Invite modal — shown after successful tenant creation */}
      <Dialog open={!!invite} onOpenChange={v => { if (!v) setInvite(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              {invite?.tenantName} is ready
            </DialogTitle>
          </DialogHeader>
          {invite && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-muted-foreground">
                <strong>{invite.adminName}</strong> sets their own password with the link below.
                It expires in {invite.expiresInDays} days.
              </p>

              <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                <CopyField label="Login Email" value={invite.adminEmail} />
                <CopyField label="Set-password Link" value={invite.inviteLink} />
                {invite.loginLink && <CopyField label="Staff Login Link" value={invite.loginLink} />}
              </div>

              {invite.emailScheduled ? (
                <div className="flex items-center gap-2 text-sm bg-success/5 text-success border border-success/30 rounded-md px-3 py-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  Invite email sent to {invite.adminEmail}.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm bg-highlight text-highlight-foreground border border-highlight-foreground/20 rounded-md px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Email is not configured — copy the message below and share it (e.g. on WhatsApp).
                </div>
              )}

              <Button
                variant="outline"
                className="w-full text-xs"
                onClick={async () => {
                  await copyToClipboard(inviteMessage(invite));
                  ok('Invite message copied to clipboard');
                }}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />Copy invite message
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInvite(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage tenant */}
      <Dialog open={!!manageTenant} onOpenChange={v => { if (!v) setManageTenant(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage — {manageTenant?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {manageUsage && (
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border rounded-lg border border-border overflow-hidden text-xs">
                {([['Users', manageUsage.users], ['Clinics', manageUsage.clinics], ['Doctors', manageUsage.doctors], ['Patients', manageUsage.patients]] as const).map(([label, n]) => (
                  <div key={label} className="bg-card px-3 py-2">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-mono text-base font-semibold tabular-nums text-foreground">{n}</dd>
                  </div>
                ))}
              </dl>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Tenant Name"><Input value={manageForm.name} onChange={e => setManageForm(f => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="Status">
                <Select value={manageForm.status} onValueChange={v => setManageForm(f => ({ ...f, status: v as typeof STATUSES[number] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Contact Email"><Input type="email" value={manageForm.email} onChange={e => setManageForm(f => ({ ...f, email: e.target.value }))} /></Field>
              <Field label="Phone"><Input value={manageForm.phone} onChange={e => setManageForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            </div>
            <Field label="Address"><Input value={manageForm.address} onChange={e => setManageForm(f => ({ ...f, address: e.target.value }))} /></Field>
            <Field label="Login link">
              <div className="flex items-center rounded-md border border-field bg-background focus-within:ring-2 focus-within:ring-ring">
                <span className="pl-3 text-sm text-muted-foreground whitespace-nowrap">{window.location.host}/c/</span>
                <input value={manageForm.subdomain} maxLength={30} autoComplete="off" spellCheck={false} aria-label="Login link name"
                  onChange={e => setManageForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
                  className="flex-1 min-w-0 h-9 bg-transparent pr-3 text-sm outline-none" />
              </div>
              <p className="text-xs text-muted-foreground">
                {manageTenant?.subdomain && manageForm.subdomain !== manageTenant.subdomain
                  ? 'The old link and any printed QR code will stop working.'
                  : 'The clinic shares this with its staff. Leave empty for no link.'}
              </p>
            </Field>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">License</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Plan">
                  <Select value={manageForm.plan} onValueChange={v => setManageForm(f => ({ ...f, plan: v as typeof PLANS[number] }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Max Users"><Input type="number" min={0} placeholder="Unlimited" value={manageForm.max_users} onChange={e => setManageForm(f => ({ ...f, max_users: e.target.value }))} /></Field>
                <Field label="Max Clinics"><Input type="number" min={0} placeholder="Unlimited" value={manageForm.max_clinics} onChange={e => setManageForm(f => ({ ...f, max_clinics: e.target.value }))} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Expires On"><Input type="date" value={manageForm.license_expires_at} onChange={e => setManageForm(f => ({ ...f, license_expires_at: e.target.value }))} /></Field>
              </div>
            </div>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">Owner Login</p>
            <p className="text-xs text-muted-foreground mb-2">
              Send the owner a new set-password link — use this if the invite expired or they forgot their password.
            </p>
            <Button variant="outline" size="sm" className="w-full text-xs" disabled={resendSaving} onClick={handleResendInvite}>
              <Link2 className="w-3.5 h-3.5 mr-1" />{resendSaving ? 'Creating link…' : 'Send set-password link'}
            </Button>
            {resendResult && (
              <div className="mt-2 space-y-2">
                <CopyField label="Set-password Link" value={resendResult.inviteLink} />
                <div className={cn('flex items-center gap-2 text-xs rounded px-2 py-1.5',
                  resendResult.emailSent ? 'bg-success/5 text-success border border-success/30' : 'bg-highlight text-highlight-foreground border border-highlight-foreground/20'
                )}>
                  {resendResult.emailSent
                    ? <><CheckCircle className="w-3.5 h-3.5" />Link emailed to {resendResult.adminEmail}.</>
                    : <><AlertTriangle className="w-3.5 h-3.5" />Email not configured — copy the link and share it with {resendResult.adminEmail}.</>}
                </div>
              </div>
            )}
          </div>

          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">On-premise License</p>
            <Button variant="outline" size="sm" className="w-full text-xs" onClick={downloadLicenseFile}>
              <Download className="w-3.5 h-3.5 mr-1" />Download license file
            </Button>
            <p className="text-xs text-muted-foreground mt-1">Signed from the saved plan, limits and expiry — save changes first.</p>
          </div>

          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2 mt-2 flex items-center gap-1"><History className="w-3.5 h-3.5" />Change History</p>
            {history === null ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-xs text-muted-foreground">No changes recorded yet.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {history.map(h => (
                  <li key={h.id} className="text-xs">
                    <div className="text-muted-foreground">
                      {formatDateTime(h.at)} · {h.actor_name || h.actor_email || 'System'}
                    </div>
                    {h.action === 'create' ? (
                      <div>Tenant created</div>
                    ) : (h.changed_fields ?? []).map(f => (
                      <div key={f}>
                        {HISTORY_FIELD_LABELS[f] ?? f}: {formatHistoryValue(f, h.before_data?.[f])} → {formatHistoryValue(f, h.after_data?.[f])}
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManageTenant(null)} disabled={manageSaving}>Cancel</Button>
            <Button onClick={handleManageSave} disabled={manageSaving}>{manageSaving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
